import { gql } from '@apollo/client';
import {
  ACTIVITY_FRAGMENT,
  ACTIVITY_SET_FRAGMENT,
  PART_ADJUSTMENT_FRAGMENT,
  WORK_ORDER_FRAGMENT,
  WORK_ORDER_OPERATIONS_FRAGMENT,
  RESOURCE_FRAGMENT,
  LABOR_TICKET_REASON_FIELDS,
  BASE_LABOR_TICKET_FIELDS_FRAGMENT,
  OPERATOR_RUN_FRAGMENT,
} from 'lib/api/fragments';

const START_OPERATOR = gql`
  ${OPERATOR_RUN_FRAGMENT}
  mutation startOperator($input: InputMutation) {
    startOperatorResponse(input: $input)
      @rest(
        type: "OperatorRun"
        method: "POST"
        path: "/operator-view/operator-run/start/{args.input.machineId}"
      ) {
      ...OperatorRunFields
    }
  }
`;

const STOP_OPERATOR = gql`
  ${OPERATOR_RUN_FRAGMENT}
  mutation stopOperator($input: InputMutation) {
    stopOperatorResponse(input: $input)
      @rest(
        type: "OperatorRun"
        method: "POST"
        path: "/operator-view/operator-run/stop/{args.input.machineId}"
      ) {
      ...OperatorRunFields
    }
  }
`;

const CREATE_OPERATION = gql`
  mutation createOperation($input: InputMutation) {
    createOperationResponse(input: $input)
      @rest(type: "JobPayload", method: "POST", path: "/operator-view/jobs") {
      id
      company_id
      name
      actualParts
      partCountMultiplier
    }
  }
`;

const START_JOB = gql`
  mutation startJob($input: InputMutation) {
    startJobResponse(input: $input)
      @rest(
        type: "Job"
        method: "POST"
        path: "/operator-view/job-dispatch/{args.input.id}/start/{args.input.jobId}"
      ) {
      result
    }
  }
`;

const STOP_JOB = gql`
  mutation stopJob($input: InputMutation) {
    stopJobResponse(input: $input)
      @rest(
        type: "Job"
        method: "POST"
        path: "/operator-view/job-dispatch/{args.input.id}/stop/{args.input.jobId}"
      ) {
      result
    }
  }
`;

const END_SETUP = gql`
  mutation endSetup($input: InputMutation) {
    endSetupResponse(input: $input)
      @rest(
        type: "EndSetup"
        method: "POST"
        path: "/operator-view/job-setup/end-setup/{args.input.jsgId}/{args.input.machineId}"
      ) {
      result
    }
  }
`;

const CREATE_PART_ADJUSTMENT = gql`
  ${PART_ADJUSTMENT_FRAGMENT}
  mutation createPartAdjustment($input: InputMutation) {
    createPartAdjustmentResponse(input: $input)
      @rest(
        type: "PartAdjustment"
        method: "POST"
        path: "/operator-view/part-adjustment"
      ) {
      ...PartAdjustmentFields
      reject_behavior
    }
  }
`;

const CREATE_ANNOTATION = gql`
  mutation createAnnotation($input: InputMutation) {
    createAnnotationResponse(input: $input)
      @rest(
        type: "AnnotationPayload"
        method: "POST"
        path: "/operator-view/annotations"
      ) {
      id
      message
      machineId
      machine_id
      category_id
      annotationTypeId
      duration
      planned
      start
      end
      isEndSet
      setup
      annotationType @type(name: "annotationType") {
        id
        name
        plannedBehavior
        plannedLimitMs
        color
      }
    }
  }
`;

const UPDATE_ANNOTATION = gql`
  mutation updateAnnotation($input: InputMutation) {
    updateAnnotationResponse(input: $input)
      @rest(
        type: "AnnotationPayload"
        method: "PUT"
        path: "/operator-view/annotations/{args.input.id}"
      ) {
      id
      message
      machineId
      machine_id
      category_id
      annotationTypeId
      duration
      planned
      start
      end
      isEndSet
      setup
      annotationType @type(name: "annotationType") {
        id
        name
        plannedBehavior
        plannedLimitMs
        color
      }
    }
  }
`;

const CREATE_ACTIVITY_SET = gql`
  ${ACTIVITY_FRAGMENT}
  ${ACTIVITY_SET_FRAGMENT}
  mutation startActivitySet($input: InputMutation) {
    createActivitySetResponse(input: $input, query: $query)
      @rest(
        type: "ActivitySet"
        method: "POST"
        path: "/operator-view/machines/{args.input.machineRef}/activity-sets?shouldCloseLatestSet={args.query.shouldCloseLatestSet}"
      ) {
      activitySet @type(name: "ActivitySet") {
        ...ActivitySetFields
        optimalUnitDuration
        activities @type(name: "Activity") {
          ...ActivityFields
        }
      }
    }
  }
`;

const CREATE_ACTIVITY_SET_WITH_WORK_ORDER = gql`
  ${ACTIVITY_FRAGMENT}
  ${ACTIVITY_SET_FRAGMENT}
  mutation startActivitySetWithWorkOrder($input: InputMutation) {
    createActivitySetWithWorkOrderResponse(input: $input, query: $query)
      @rest(
        type: "ActivitySet"
        method: "POST"
        path: "/operator-view/machines/{args.input.machineRef}/work-order/activity-sets?shouldCloseLatestSet={args.query.shouldCloseLatestSet}"
      ) {
      activitySet @type(name: "ActivitySet") {
        ...ActivitySetFields
        optimalUnitDuration
        activities @type(name: "Activity") {
          ...ActivityFields
        }
      }
    }
  }
`;

const START_ACTIVITY_WITH_LABOR_TICKET = gql`
  ${ACTIVITY_FRAGMENT}
  ${ACTIVITY_SET_FRAGMENT}
  ${BASE_LABOR_TICKET_FIELDS_FRAGMENT}
  ${LABOR_TICKET_REASON_FIELDS}
  mutation startActivityWithLaborTicket($input: InputMutation) {
    startActivityWithLaborTicketResponse(input: $input, query: $query)
      @rest(
        type: "ActivitySet"
        method: "POST"
        path: "/operator-view/labor-ticket/submit"
      ) {
      createActivitySet {
        activitySet @type(name: "ActivitySet") {
          ...ActivitySetFields
          optimalUnitDuration
          activities @type(name: "Activity") {
            ...ActivityFields
          }
        }
      }
      createLaborTicket {
        ...BaseLaborTicketFields
        reasons {
          ...LaborTicketReasonFields
        }
      }
    }
  }
`;

// Async-test variant of starting activity with labor ticket. Identical payload/shape,
// only the REST path differs to target the async-test endpoint.
const START_ACTIVITY_WITH_LABOR_TICKET_NATS = gql`
  ${BASE_LABOR_TICKET_FIELDS_FRAGMENT}
  ${LABOR_TICKET_REASON_FIELDS}
  ${WORK_ORDER_OPERATIONS_FRAGMENT}
  ${WORK_ORDER_FRAGMENT}
  ${RESOURCE_FRAGMENT}

  fragment LaborTicketWithDetails on erpLaborTickets {
    ...BaseLaborTicketFields
    reasons {
      ...LaborTicketReasonFields
    }
    workOrderOperation {
      ...WorkOrderOperationsFields
      matchingPartOperations {
        cycleTimeMs
        description
        partName
        quantityPerPart
        setupTimeMs
        partOperationRef
        partNumber
        sequenceNumber
      }
      workOrder {
        ...WorkOrderFields
        dueDate
      }
      resource {
        ...ResourceFields
      }
    }
  }

  mutation startActivityWithLaborTicket($input: InputMutation) {
    startActivityWithLaborTicketResponse(input: $input)
      @rest(
        type: "NatsLaborTicketSubmitResult"
        method: "POST"
        path: "/operator-view/labor-ticket/submit-nats"
      ) {
      success
      messageId
      status
      message
      error
      actionResults {
        createLaborTicket {
          ...LaborTicketWithDetails
        }
        updateLaborTicket {
          ...LaborTicketWithDetails
        }
        openLaborTicket {
          ...LaborTicketWithDetails
        }
        closeLaborTicket {
          ...LaborTicketWithDetails
        }
        startOperatorRun {
          id
          startAt
          endAt
          operatorId
          companyId
          machineId
          createdAt
          updatedAt
          operator {
            id
            name
          }
          machine {
            id
            name
          }
        }
      }
      erpResponses {
        action
        error
        response {
          status
          error {
            message
          }
          laborTicket {
            ...LaborTicketWithDetails
          }
        }
      }
    }
  }
`;

const SUBMIT_LABOR_TICKET_TRANSACTION = gql`
  ${OPERATOR_RUN_FRAGMENT}
  ${ACTIVITY_FRAGMENT}
  ${ACTIVITY_SET_FRAGMENT}
  ${BASE_LABOR_TICKET_FIELDS_FRAGMENT}
  ${LABOR_TICKET_REASON_FIELDS}
  ${PART_ADJUSTMENT_FRAGMENT}
  mutation submitLaborTicketTransaction($input: InputMutation) {
    submitLaborTicketTransactionResponse(input: $input, query: $query)
      @rest(
        type: "ActivitySet"
        method: "POST"
        path: "/operator-view/labor-ticket/submit"
      ) {
      startOperatorRun {
        ...OperatorRunFields
      }
      rejectParts {
        ...PartAdjustmentFields
      }
      partAdjustment {
        ...PartAdjustmentFields
      }
      createActivity @type(name: "Activity") {
        ...ActivityFields
        name
      }
      closeLaborTicket {
        ...BaseLaborTicketFields
        reasons {
          ...LaborTicketReasonFields
        }
      }
      createLaborTicket {
        ...BaseLaborTicketFields
        reasons {
          ...LaborTicketReasonFields
        }
      }
    }
  }
`;

const UPDATE_ACTIVITY_SET = gql`
  ${ACTIVITY_SET_FRAGMENT}
  ${ACTIVITY_FRAGMENT}
  mutation updateActivitySet(
    $input: InputMutation
    $query: QueryInput = { updateFromWorkOrder: false }
  ) {
    updateActivitySetResponse(input: $input, query: $query)
      @rest(
        type: "ActivitySet"
        method: "PUT"
        path: "/operator-view/machines/{args.input.machineRef}/activity-sets/{args.input.activitySetRef}?updateFromWorkOrder={args.query.updateFromWorkOrder}"
      ) {
      activitySet @type(name: "ActivitySet") {
        ...ActivitySetFields
        activities @type(name: "Activity") {
          ...ActivityFields
        }
      }
    }
  }
`;

const CREATE_ACTIVITY = gql`
  ${ACTIVITY_FRAGMENT}
  mutation createActivity($input: InputMutation) {
    createActivityResponse(input: $input)
      @rest(
        type: "Activity"
        method: "POST"
        path: "/operator-view/activity-sets/{args.input.activitySetRef}/activities"
      ) {
      activity @type(name: "Activity") {
        ...ActivityFields
        name
      }
    }
  }
`;

const UPDATE_ACTIVITY = gql`
  ${ACTIVITY_FRAGMENT}
  mutation updateActivity($input: InputMutation) {
    updateActivityResponse(input: $input)
      @rest(
        type: "Activity"
        method: "PUT"
        path: "/operator-view/activity-sets/{args.input.activitySetRef}/activities/{args.input.activityRef}"
      ) {
      activity @type(name: "Activity") {
        ...ActivityFields
      }
    }
  }
`;

const TRIGGER_WORKFLOW = gql`
  mutation triggerWorkflow($input: InputMutation) {
    triggerWorkflowResponse(input: $input)
      @rest(
        type: "TriggerWorkflowResult"
        method: "POST"
        path: "/operator-view/monitors/trigger"
      ) {
      actionResults @type(name: "ActionResult") {
        actionType
        status
        errorCode
      }
    }
  }
`;

const TIMESYNC = gql`
  mutation timesync($input: InputMutation) {
    timesyncData(input: $input)
      @rest(type: "Timesync", path: "/timesync", method: "POST") {
      result
      id
    }
  }
`;

const CREATE_LABOR_TICKET = gql`
  ${BASE_LABOR_TICKET_FIELDS_FRAGMENT}
  ${LABOR_TICKET_REASON_FIELDS}
  mutation insertErpLaborTicket($input: erpLaborTickets_insert_input!) {
    insertErpLaborTicket(object: $input) {
      ...BaseLaborTicketFields
      reasons {
        ...LaborTicketReasonFields
      }
    }
  }
`;

const DELETE_LABOR_TICKET = gql`
  mutation deleteErpLaborTicket($ref: bigint!) {
    deleteErpLaborTicket(laborTicketRef: $ref) {
      laborTicketRef
    }
  }
`;

const UPDATE_LABOR_TICKET = gql`
  ${BASE_LABOR_TICKET_FIELDS_FRAGMENT}
  mutation updateErpLaborTicket(
    $laborTicketRef: bigint!
    $input: erpLaborTickets_set_input
  ) {
    updateErpLaborTicket(
      pk_columns: { laborTicketRef: $laborTicketRef }
      _set: $input
    ) {
      ...BaseLaborTicketFields
      comment
    }
  }
`;

// workOrderOperation's startQuantity is actually currentQuantity, currently we do not have any insight for the erp _start quantity_.
// Renaming this variable to currentQuantity avoid confusion on the frontend
const UPDATE_ORDER_OPERATION = gql`
  ${WORK_ORDER_FRAGMENT}
  ${WORK_ORDER_OPERATIONS_FRAGMENT}
  ${RESOURCE_FRAGMENT}
  mutation updateErpWorkOrderOperation(
    $workOrderOperationRef: bigint!
    $input: erpWorkOrderOperations_set_input
  ) {
    updateErpWorkOrderOperation(
      pk_columns: { workOrderOperationRef: $workOrderOperationRef }
      _set: $input
    ) {
      ...WorkOrderOperationsFields
      workOrder {
        ...WorkOrderFields
      }
      resource {
        ...ResourceFields
      }
    }
  }
`;

const UPDATE_VIA_INSERT_LABOR_TICKET = gql`
  ${BASE_LABOR_TICKET_FIELDS_FRAGMENT}
  mutation updateViaInsertErpLaborTicket(
    $input: erpLaborTickets_insert_input!
    $columns: [erpLaborTickets_update_column!]
  ) {
    insertErpLaborTicket(
      object: $input
      on_conflict: { constraint: labor_ticket_pk, update_columns: $columns }
    ) {
      comment
      ...BaseLaborTicketFields
      reasons {
        reasonRef
      }
    }
  }
`;

const CREATE_MANUAL_PART = gql`
  mutation createManualPart($input: InputMutation) {
    createManualPartResponse(input: $input)
      @rest(
        type: "Activity"
        method: "POST"
        path: "/operator-view/manual-part"
      ) {
      success
    }
  }
`;

const SET_TIMESHEET_CLOCK_OUT = gql`
  mutation setTimesheetClockOut($personId: String!, $clockOut: timestamptz!) {
    updateErpTimesheets(
      where: { personId: { _eq: $personId }, clockOut: { _is_null: true } }
      _set: { clockOut: $clockOut }
    ) {
      affected_rows
      returning {
        timesheetRef
        personId
        clockIn
        clockOut
      }
    }
  }
`;

const VERIFY_OPERATOR_PIN = gql`
  mutation verifyOperatorPin($input: InputMutation) {
    verifyPinResponse(input: $input)
      @rest(
        type: "PinVerification"
        method: "POST"
        path: "/operator-view/operator/{args.input.operatorId}/verify-pin"
      ) {
      valid
      error
      delay
      attempts
      isInitialPin
      pinExpiration
    }
  }
`;

export {
  CREATE_ANNOTATION,
  CREATE_PART_ADJUSTMENT,
  END_SETUP,
  CREATE_OPERATION,
  START_JOB,
  START_OPERATOR,
  STOP_JOB,
  STOP_OPERATOR,
  UPDATE_ANNOTATION,
  CREATE_ACTIVITY_SET,
  UPDATE_ACTIVITY_SET,
  CREATE_ACTIVITY,
  UPDATE_ACTIVITY,
  TRIGGER_WORKFLOW,
  TIMESYNC,
  CREATE_LABOR_TICKET,
  DELETE_LABOR_TICKET,
  UPDATE_LABOR_TICKET,
  CREATE_ACTIVITY_SET_WITH_WORK_ORDER,
  START_ACTIVITY_WITH_LABOR_TICKET,
  START_ACTIVITY_WITH_LABOR_TICKET_NATS,
  SUBMIT_LABOR_TICKET_TRANSACTION,
  UPDATE_VIA_INSERT_LABOR_TICKET,
  UPDATE_ORDER_OPERATION,
  CREATE_MANUAL_PART,
  SET_TIMESHEET_CLOCK_OUT,
  VERIFY_OPERATOR_PIN,
};
